(function () {
  const script = /** @type {HTMLScriptElement|null} */ (
    document.currentScript || document.querySelector('script[src*="cart-insurance"]')
  );
  const INSURANCE_ID = Number(script?.dataset?.insuranceId || 39407882305578);
  const ROOT_SELECTOR = '[data-cart-insurance-opt-in]';
  const CHECKBOX_SELECTOR = '.cart-shipping-protection__checkbox';
  const STATUS_SELECTOR = '.cart-shipping-protection__status';

  /** @typedef {{ variant_id: number, quantity: number }} CartItem */
  /** @typedef {{ items?: CartItem[], sections?: Record<string, string> }} Cart */

  function getSectionIds() {
    return Array.from(
      new Set(
        Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : '')
          .filter(Boolean)
      )
    );
  }

  /** @param {Cart} cart */
  function merchandiseItemCount(cart) {
    return (cart.items || []).reduce((count, item) => {
      return item.variant_id === INSURANCE_ID ? count : count + Number(item.quantity || 0);
    }, 0);
  }

  /** @param {Cart} cart */
  function hasInsurance(cart) {
    return (cart.items || []).some((item) => item.variant_id === INSURANCE_ID && item.quantity > 0);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not fetch cart');
    return response.json();
  }

  /**
   * @param {string} endpoint
   * @param {Record<string, unknown>} body
   */
  async function mutateCart(endpoint, body) {
    const sectionIds = getSectionIds();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await response.json();
    if (!response.ok || payload.errors) {
      throw new Error(typeof payload.errors === 'string' ? payload.errors : 'Cart update failed');
    }

    const cart = await fetchCart();
    cart.sections = payload.sections || {};
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'cart-insurance',
          data: {
            source: 'cart-insurance',
            itemCount: merchandiseItemCount(cart),
            sections: cart.sections,
          },
        },
      })
    );
    return cart;
  }

  async function addInsurance() {
    const cart = await fetchCart();
    if (hasInsurance(cart)) return cart;
    return mutateCart('/cart/add.js', { id: INSURANCE_ID, quantity: 1 });
  }

  async function removeInsurance() {
    return mutateCart('/cart/update.js', { updates: { [INSURANCE_ID]: 0 } });
  }

  /**
   * @param {HTMLElement} root
   * @param {string} message
   */
  function setStatus(root, message) {
    const status = root.querySelector(STATUS_SELECTOR);
    if (status) status.textContent = message;
  }

  /** @param {HTMLElement} root */
  function bindOptIn(root) {
    if (root.dataset.cartInsuranceBound === 'true') return;
    const checkbox = root.querySelector(CHECKBOX_SELECTOR);
    if (!(checkbox instanceof HTMLInputElement)) return;

    root.dataset.cartInsuranceBound = 'true';
    checkbox.addEventListener('change', async () => {
      const targetCheckedState = checkbox.checked;
      checkbox.disabled = true;
      root.setAttribute('aria-busy', 'true');
      setStatus(root, targetCheckedState ? 'Adding shipping protection…' : 'Removing shipping protection…');

      try {
        await (targetCheckedState ? addInsurance() : removeInsurance());
        setStatus(root, targetCheckedState ? 'Shipping protection added.' : 'Shipping protection removed.');
      } catch (error) {
        checkbox.checked = !targetCheckedState;
        setStatus(root, 'Shipping protection could not be updated. Please try again.');
        console.error('Shipping protection update failed', error);
      } finally {
        checkbox.disabled = false;
        root.removeAttribute('aria-busy');
      }
    });
  }

  function bindOptIns() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement) bindOptIn(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOptIns, { once: true });
  } else {
    bindOptIns();
  }

  document.addEventListener('cart:update', () => {
    window.setTimeout(bindOptIns, 0);
  });

  new MutationObserver(bindOptIns).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
